import React, { useState, useRef, useEffect } from 'react';
import { ArrowUp, Plus, Image, Video, Sparkles, Wand2, Palette, Zap, Trash2 } from 'lucide-react';
import { spacing, shadows } from '../../constants/designTokens';
import { useTheme } from '../../contexts/ThemeContext';
import { RENDER_CONFIG } from '../../constants/renderConfig';
import brandIcon from '../../assets/ai-image-ascii.png';
import ChatMessageList from './ChatMessageList';
import ASCIIPreview from './ASCIIPreview';
import SimpleTooltip from '../ui/SimpleTooltip';
import ConfirmModal from '../modals/ConfirmModal';

/**
 * Custom brand icon
 */
const BrandIcon = ({ size = 32 }) => (
  <img
    src={brandIcon}
    alt=""
    width={size}
    height={size}
    style={{ objectFit: 'contain' }}
  />
);

/**
 * Get time-based greeting
 */
const getGreeting = () => {
  const hour = new Date().getHours();
  if (hour >= 5 && hour < 12) return 'Good to see you';
  if (hour >= 12 && hour < 17) return 'Good afternoon';
  if (hour >= 17 && hour < 21) return 'Good evening';
  return 'Hello, night owl';
};

/**
 * Quick action suggestions for the user
 */
const quickActions = [
  { icon: Sparkles, label: 'Innovation', prompt: 'Innovation emerging from collaboration' },
  { icon: Wand2, label: 'Abstract', prompt: 'Abstract geometric patterns flowing like water' },
  { icon: Palette, label: 'Nature', prompt: 'Organic growth patterns inspired by nature' },
  { icon: Zap, label: 'Energy', prompt: 'Dynamic energy bursting with momentum' },
];

/**
 * CreativePromptView Component
 *
 * Claude-like chat interface for AI pattern generation with media upload options.
 * Shown when in creative mode before a pattern is generated.
 */
const CreativePromptView = ({
  prompt,
  onPromptChange,
  isGenerating,
  isDisabled = false,
  disabledUntil = null,
  onGenerate,
  onImageUpload,
  onVideoUpload,
  messages = [],
  selectedMessageId = null,
  onSelectMessage = () => {},
  // ASCII Preview props for selected message
  asciiText = null,
  columns,
  isFullscreen,
  onFullscreenClose,
  onFreeze,
  onUnfreeze,
  isFrozen,
  backgroundColor,
  foregroundColor,
  // When true, a media upload exists — text generation becomes locked
  hasUploadedMedia = false,
  uploadedImageUrl = null,
  uploadedVideoUrl = null,
  isMediaReady = false,
  // Clear history handler
  onClearHistory = () => {},
}) => {
  const [showClearConfirm, setShowClearConfirm] = useState(false);
  const { colors } = useTheme();
  const [showAttachMenu, setShowAttachMenu] = useState(false);
  const [isFocused, setIsFocused] = useState(false);
  const [pendingPrompt, setPendingPrompt] = useState(null);
  const textareaRef = useRef(null);
  const attachMenuRef = useRef(null);
  const attachButtonRef = useRef(null);
  const scrollRef = useRef(null);
  const hasHistory = messages.length > 0;

  // Track if user has submitted - shows chat view immediately
  const hasStartedChat = hasHistory || pendingPrompt !== null;

  // Clear pending prompt when it appears in messages
  useEffect(() => {
    if (pendingPrompt && messages.some(m => m.type === 'user' && m.prompt === pendingPrompt)) {
      setPendingPrompt(null);
    }
  }, [messages, pendingPrompt]);

  // Auto-scroll to bottom when new messages arrive or when generating
  useEffect(() => {
    if (scrollRef.current && (hasStartedChat || isGenerating)) {
      // Small delay to allow DOM to update
      setTimeout(() => {
        if (scrollRef.current) {
          scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
        }
      }, 50);
    }
  }, [messages.length, hasStartedChat, isGenerating, pendingPrompt]);

  // Calculate remaining time if disabled
  const [remainingTime, setRemainingTime] = useState(null);

  useEffect(() => {
    if (isDisabled && disabledUntil) {
      const updateTimer = () => {
        const now = Date.now();
        const remaining = Math.max(0, disabledUntil - now);
        if (remaining > 0) {
          const minutes = Math.floor(remaining / 60000);
          const seconds = Math.floor((remaining % 60000) / 1000);
          setRemainingTime(`${minutes}:${seconds.toString().padStart(2, '0')}`);
        } else {
          setRemainingTime(null);
        }
      };

      updateTimer();
      const interval = setInterval(updateTimer, 1000);
      return () => clearInterval(interval);
    } else {
      setRemainingTime(null);
    }
  }, [isDisabled, disabledUntil]);

  // Close attach menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        showAttachMenu &&
        attachMenuRef.current &&
        !attachMenuRef.current.contains(event.target) &&
        attachButtonRef.current &&
        !attachButtonRef.current.contains(event.target)
      ) {
        setShowAttachMenu(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [showAttachMenu]);

  // Auto-resize textarea
  useEffect(() => {
    if (textareaRef.current) {
      textareaRef.current.style.height = 'auto';
      textareaRef.current.style.height = Math.min(textareaRef.current.scrollHeight, 200) + 'px';
    }
  }, [prompt]);

  const handleQuickAction = (actionPrompt) => {
    onPromptChange(actionPrompt);
    textareaRef.current?.focus();
  };

  // Wrapper to capture prompt before generating
  const handleGenerate = () => {
    if (prompt.trim()) {
      setPendingPrompt(prompt.trim());
    }
    onGenerate();
  };

  const canSubmit = prompt.trim() && !isGenerating && !isDisabled && !hasUploadedMedia;

  return (
    <div style={{
      display: 'flex',
      flexDirection: 'column',
      width: '100%',
      maxWidth: '720px',
      margin: '0 auto',
      height: hasStartedChat ? '100%' : 'auto',
      boxSizing: 'border-box',
    }}>
      {/* Greeting - only show if chat hasn't started */}
      {!hasStartedChat && (
        <>
          <div style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: spacing.md,
            marginBottom: spacing.lg,
            padding: `${spacing.xl} ${spacing.xl} 0`,
          }}>
            <BrandIcon size={36} />
            <h1 style={{
              fontSize: 'clamp(1.75rem, 4vw, 2.5rem)',
              fontWeight: 400,
              color: colors.foreground,
              letterSpacing: '-0.02em',
              margin: 0,
              fontFamily: 'Georgia, "Times New Roman", serif',
            }}>
              {getGreeting()}
            </h1>
          </div>

          {/* Info section */}
          <p style={{
            fontSize: '0.9375rem',
            color: colors.foregroundSecondary,
            textAlign: 'center',
            lineHeight: 1.5,
            marginBottom: spacing.xxl,
            maxWidth: '480px',
            margin: `0 auto ${spacing.xxl}`,
            padding: `0 ${spacing.xl}`,
          }}>
            Type a vibe, upload media and I'll generate an ASCII pattern.
          </p>
        </>
      )}

      {/* Show ASCII preview for uploaded media when chat hasn't started */}
      {asciiText && !hasStartedChat && (
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: spacing.xl, marginBottom: spacing.xl }}>
          <ASCIIPreview
            asciiText={asciiText}
            columns={columns}
            onFreeze={onFreeze}
            onUnfreeze={onUnfreeze}
            isFrozen={isFrozen}
            backgroundColor={backgroundColor}
            foregroundColor={foregroundColor}
            isFullscreen={isFullscreen}
            onFullscreenClose={onFullscreenClose}
          />
        </div>
      )}

      {/* Chat History - show if chat has started */}
      {hasStartedChat && (
        <div
          ref={scrollRef}
          style={{
            flex: 1,
            overflowY: 'auto',
            padding: spacing.xl,
          }}
        >
          <ChatMessageList
            messages={messages}
            selectedMessageId={selectedMessageId}
            onSelectMessage={onSelectMessage}
            asciiText={asciiText}
            columns={columns}
            isFullscreen={isFullscreen}
            onFullscreenClose={onFullscreenClose}
            onFreeze={onFreeze}
            onUnfreeze={onUnfreeze}
            isFrozen={isFrozen}
            backgroundColor={backgroundColor}
            foregroundColor={foregroundColor}
            isGenerating={isGenerating}
            pendingPrompt={pendingPrompt}
          />
        </div>
      )}

      {/* Confirm modal for clearing history */}
      <ConfirmModal
        isOpen={showClearConfirm}
        title="Clear chat history"
        description="This will remove all messages and reset the current session. This cannot be undone."
        confirmLabel="Clear"
        cancelLabel="Cancel"
        danger={true}
        onCancel={() => setShowClearConfirm(false)}
        onConfirm={() => {
          setShowClearConfirm(false);
          setPendingPrompt(null);
          onClearHistory();
        }}
      />

      {/* Input Section - fixed at bottom when chat has started */}
      <div style={{
        padding: hasStartedChat ? `${spacing.md} ${spacing.xl} ${spacing.xl}` : `0 ${spacing.xl}`,
        borderTop: hasStartedChat ? `1px solid ${colors.border}` : 'none',
        background: hasStartedChat ? colors.background : 'transparent',
      }}>
        {/* When media is ready, hide input and show minimal reset bar */}
        {isMediaReady ? (
          <div style={{
            display: 'flex',
            justifyContent: 'center',
            padding: spacing.md,
          }}>
            <button
              onClick={() => {
                setPendingPrompt(null);
                onClearHistory();
              }}
              aria-label="Clear media and start over"
              onMouseEnter={(e) => {
                e.currentTarget.style.background = '#FDE8E8';
                e.currentTarget.style.color = '#C0392B';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.background = 'transparent';
                e.currentTarget.style.color = colors.foregroundSecondary;
              }}
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '6px',
                height: '36px',
                padding: '0 14px',
                background: 'transparent',
                border: `1px solid ${colors.border}`,
                color: colors.foregroundSecondary,
                cursor: 'pointer',
                borderRadius: '4px',
                transition: 'all 0.15s ease',
                fontSize: '0.8125rem',
                fontWeight: 500,
                letterSpacing: '-0.005em',
                fontFamily: 'Sohne, -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif',
                outline: 'none',
              }}
            >
              <Trash2 size={14} />
              Clear media and start over
            </button>
          </div>
        ) : (
          <>
            {/* Chat Input Container */}
            <div style={{
              width: '100%',
              background: colors.background,
              borderRadius: '12px',
              border: `1px solid ${isFocused ? colors.foreground : colors.border}`,
              boxShadow: isFocused ? `0 0 0 4px ${colors.backgroundOffset}` : '0 1px 2px rgba(0,0,0,0.05)',
              transition: 'all 0.15s ease',
            }}>
            {/* Text Input Area */}
            <div style={{
              padding: `${spacing.lg} ${spacing.lg} ${spacing.sm}`,
            }}>
              {/* Show thumbnail if media is uploading */}
              {hasUploadedMedia && !isMediaReady && (uploadedImageUrl || uploadedVideoUrl) ? (
                <div style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: spacing.md,
                  padding: spacing.md,
                  background: colors.backgroundOffset,
                  borderRadius: '8px',
                  marginBottom: spacing.sm,
                }}>
                  <img
                    src={uploadedImageUrl || uploadedVideoUrl}
                    alt="Uploaded media"
                    style={{
                      width: '80px',
                      height: '80px',
                      objectFit: 'cover',
                      borderRadius: '4px',
                      border: `1px solid ${colors.border}`,
                    }}
                  />
                  <div style={{ flex: 1 }}>
                    <div style={{ fontSize: '0.875rem', fontWeight: 500, color: colors.foreground, marginBottom: spacing.xs }}>
                      {uploadedImageUrl ? 'Image' : 'Video'} uploaded
                    </div>
                    <div style={{ fontSize: '0.8125rem', color: colors.foregroundSecondary }}>
                      Processing media...
                    </div>
                  </div>
                </div>
              ) : (
                <textarea
                  ref={textareaRef}
                  value={prompt}
                  onChange={(e) => onPromptChange(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' && !e.shiftKey && canSubmit) {
                      e.preventDefault();
                      handleGenerate();
                    }
                  }}
                  onFocus={() => setIsFocused(true)}
                  onBlur={() => setIsFocused(false)}
                  placeholder={hasUploadedMedia ? "Editing uploaded media — text generation disabled" : "How can I help you today?"}
                  disabled={isDisabled || hasUploadedMedia}
                  maxLength={RENDER_CONFIG.maxPromptLength}
                  rows={1}
                  style={{
                    width: '100%',
                    minHeight: '24px',
                    maxHeight: '200px',
                    padding: 0,
                    fontSize: '1rem',
                    fontWeight: 500,
                    fontFamily: 'Sohne, -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif',
                    lineHeight: '1.5',
                    border: 'none',
                    background: 'transparent',
                    resize: 'none',
                    color: colors.foreground,
                    outline: 'none',
                    overflow: 'auto',
                  }}
                />
              )}

              {hasUploadedMedia && !isMediaReady && !(uploadedImageUrl || uploadedVideoUrl) && (
                <div style={{ fontSize: '0.875rem', color: colors.foregroundSecondary, marginTop: spacing.sm }}>
                  Media uploaded — text generation is disabled. Use the controls to edit the media-based pattern.
                </div>
              )}
            </div>

        {/* Bottom Bar with Controls */}
        <div style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          padding: `${spacing.sm} ${spacing.md}`,
          borderTop: 'none',
        }}>
          {/* Left Controls */}
          <div style={{
            display: 'flex',
            alignItems: 'center',
            gap: spacing.xs,
            position: 'relative',
          }}>
            {/* Attach Button */}
            <button
              ref={attachButtonRef}
              onClick={() => setShowAttachMenu(!showAttachMenu)}
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                width: '36px',
                height: '36px',
                padding: 0,
                background: showAttachMenu ? colors.backgroundOffset : 'transparent',
                border: `1px solid ${colors.border}`,
                borderRadius: '4px',
                cursor: 'pointer',
                color: colors.foregroundSecondary,
                transition: 'all 0.15s ease',
                outline: 'none',
              }}
              onMouseEnter={(e) => {
                if (!showAttachMenu) e.currentTarget.style.background = colors.backgroundOffset;
              }}
              onMouseLeave={(e) => {
                if (!showAttachMenu) e.currentTarget.style.background = 'transparent';
              }}
              title="Attach media"
            >
              <Plus size={18} />
            </button>

            {/* Attach Menu Dropdown */}
            {showAttachMenu && (
              <div
                ref={attachMenuRef}
                style={{
                  position: 'absolute',
                  bottom: '100%',
                  left: 0,
                  marginBottom: spacing.sm,
                  background: colors.backgroundPanel,
                  borderRadius: '4px',
                  border: `1px solid ${colors.border}`,
                  boxShadow: shadows.md,
                  overflow: 'hidden',
                  zIndex: 100,
                  minWidth: '160px',
                }}
              >
                <label style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: spacing.sm,
                  padding: `${spacing.sm} ${spacing.md}`,
                  cursor: 'pointer',
                  color: colors.foreground,
                  fontSize: '0.875rem',
                  transition: 'background 0.15s',
                }}
                onMouseEnter={(e) => e.currentTarget.style.background = colors.backgroundOffset}
                onMouseLeave={(e) => e.currentTarget.style.background = 'transparent'}
                >
                  <Image size={18} />
                  <span>Upload Image</span>
                  <input
                    type="file"
                    accept="image/*"
                    onChange={(e) => {
                      onImageUpload(e);
                      setShowAttachMenu(false);
                    }}
                    style={{ display: 'none' }}
                  />
                </label>
                <label style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: spacing.sm,
                  padding: `${spacing.sm} ${spacing.md}`,
                  cursor: 'pointer',
                  color: colors.foreground,
                  fontSize: '0.875rem',
                  transition: 'background 0.15s',
                }}
                onMouseEnter={(e) => e.currentTarget.style.background = colors.backgroundOffset}
                onMouseLeave={(e) => e.currentTarget.style.background = 'transparent'}
                >
                  <Video size={18} />
                  <span>Upload Video</span>
                  <input
                    type="file"
                    accept="video/mp4,video/webm,video/quicktime"
                    onChange={(e) => {
                      onVideoUpload(e);
                      setShowAttachMenu(false);
                    }}
                    style={{ display: 'none' }}
                  />
                </label>
              </div>
            )}

            {/* Character count */}
            {prompt.length > 0 && (
              <span style={{
                fontSize: '0.75rem',
                color: prompt.length > RENDER_CONFIG.characterLimitWarning
                  ? colors.foreground
                  : colors.foregroundTertiary,
                fontWeight: prompt.length > RENDER_CONFIG.characterLimitWarning ? 500 : 400,
                marginLeft: spacing.sm,
              }}>
                {prompt.length}/{RENDER_CONFIG.maxPromptLength}
              </span>
            )}
          </div>

          {/* Right Controls */}
          <div style={{
            display: 'flex',
            alignItems: 'center',
            gap: spacing.md,
          }}>
            {hasStartedChat && (
              <SimpleTooltip content="Clear history">
                <button
                  onClick={() => setShowClearConfirm(true)}
                  aria-label="Clear history"
                  onMouseEnter={(e) => {
                    e.currentTarget.style.background = '#FDE8E8';
                    e.currentTarget.style.color = '#C0392B';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.background = 'transparent';
                    e.currentTarget.style.color = colors.foregroundTertiary;
                  }}
                  style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    width: 36,
                    height: 36,
                    padding: 0,
                    background: 'transparent',
                    border: 'none',
                    color: colors.foregroundTertiary,
                    cursor: 'pointer',
                    borderRadius: 8,
                    transition: 'all 0.12s ease'
                  }}
                >
                  <Trash2 size={16} />
                </button>
              </SimpleTooltip>
            )}
            {/* Submit Button */}
            <button
              onClick={handleGenerate}
              disabled={!canSubmit}
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                width: '36px',
                height: '36px',
                padding: 0,
                background: canSubmit ? '#6D916C' : colors.backgroundOffset,
                border: 'none',
                borderRadius: '4px',
                cursor: canSubmit ? 'pointer' : 'not-allowed',
                color: canSubmit ? 'white' : colors.foregroundTertiary,
                transition: 'all 0.15s ease',
                position: 'relative',
                overflow: 'hidden',
                outline: 'none',
              }}
              onMouseEnter={(e) => {
                if (canSubmit) {
                  e.currentTarget.style.background = '#5A7A59';
                }
              }}
              onMouseLeave={(e) => {
                if (canSubmit) {
                  e.currentTarget.style.background = '#6D916C';
                }
              }}
              title={isDisabled && remainingTime ? `Available in ${remainingTime}` : 'Generate pattern'}
            >
              {isGenerating ? (
                <>
                  <style>{`
                    @keyframes spin {
                      to { transform: rotate(360deg); }
                    }
                  `}</style>
                  <div style={{
                    width: '18px',
                    height: '18px',
                    border: '2px solid rgba(255,255,255,0.3)',
                    borderTopColor: 'white',
                    borderRadius: '50%',
                    animation: 'spin 0.8s linear infinite',
                  }} />
                </>
              ) : (
                <ArrowUp size={20} />
              )}
            </button>
          </div>
        </div>

        {/* Disabled timer message */}
        {isDisabled && remainingTime && (
          <div style={{
            padding: `${spacing.sm} ${spacing.lg}`,
            borderTop: `1px solid ${colors.border}`,
            fontSize: '0.75rem',
            color: colors.foregroundSecondary,
            textAlign: 'center',
          }}>
            Available in {remainingTime}
          </div>
        )}
        </div>
        </>
        )}

        {/* Quick Actions - only show if chat hasn't started and no uploaded media */}
        {!hasStartedChat && !hasUploadedMedia && (
          <div style={{
            display: 'flex',
            flexWrap: 'wrap',
            justifyContent: 'center',
            gap: spacing.sm,
            marginTop: spacing.xl,
          }}>
            {quickActions.map((action, index) => {
              const Icon = action.icon;
              return (
                <button
                  key={index}
                  onClick={() => handleQuickAction(action.prompt)}
                  style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: '6px',
                    height: '32px',
                    padding: '0 12px',
                    background: 'transparent',
                    border: `1px solid ${colors.border}`,
                    borderRadius: '4px',
                    cursor: 'pointer',
                    color: colors.foreground,
                    fontSize: '0.75rem',
                    fontWeight: 500,
                    letterSpacing: '-0.005em',
                    fontFamily: 'Sohne, -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif',
                    transition: 'all 0.15s ease',
                    whiteSpace: 'nowrap',
                    outline: 'none',
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.background = colors.backgroundOffset;
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.background = 'transparent';
                  }}
                >
                  <Icon size={14} style={{ opacity: 0.7 }} />
                  <span>{action.label}</span>
                </button>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};

export default React.memo(CreativePromptView);
